import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { ACTION_SUBJECT, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import { ErrorBoundary } from '@atlaskit/editor-common/error-boundary';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { logException } from '@atlaskit/editor-common/monitoring';
import { EditorToolbarProvider, EditorToolbarUIProvider } from '@atlaskit/editor-common/toolbar';
import { Popup } from '@atlaskit/editor-common/ui';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { calculateToolbarPositionTrackHead, calculateToolbarPositionOnCellSelection } from '@atlaskit/editor-common/utils';
import { AllSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { ToolbarSection, ToolbarButtonGroup, ToolbarDropdownItemSection, useToolbarUI } from '@atlaskit/editor-toolbar';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { SELECTION_TOOLBAR_LABEL } from '../consts';
import { getKeyboardNavigationConfig } from './keyboard-config';
import { getDomRefFromSelection } from './utils';
var isToolbarComponent = function isToolbarComponent(component) {
  return component.type === 'toolbar' && component.key === 'inline-text-toolbar';
};
var usePluginState = conditionalHooksFactory(function () {
  return expValEquals('platform_editor_toolbar_aifc_patch_3', 'isEnabled', true);
}, function (api) {
  return useSharedPluginStateWithSelector(api, ['connectivity', 'userPreferences', 'toolbar', 'selection', 'userIntent', 'editorViewMode'], function (state) {
    var _state$connectivitySt, _state$userPreference, _state$toolbarState, _state$toolbarState2, _state$selectionState, _state$userIntentStat, _state$editorViewMode;
    return {
      connectivityStateMode: (_state$connectivitySt = state.connectivityState) === null || _state$connectivitySt === void 0 ? void 0 : _state$connectivitySt.mode,
      editorToolbarDockingPreference: (_state$userPreference = state.userPreferencesState) === null || _state$userPreference === void 0 || (_state$userPreference = _state$userPreference.preferences) === null || _state$userPreference === void 0 ? void 0 : _state$userPreference.toolbarDockingPosition,
      shouldShowToolbar: (_state$toolbarState = state.toolbarState) === null || _state$toolbarState === void 0 ? void 0 : _state$toolbarState.shouldShowToolbar,
      selectedNode: (_state$toolbarState2 = state.toolbarState) === null || _state$toolbarState2 === void 0 ? void 0 : _state$toolbarState2.selectedNode,
      selection: (_state$selectionState = state.selectionState) === null || _state$selectionState === void 0 ? void 0 : _state$selectionState.selection,
      currentUserIntent: (_state$userIntentStat = state.userIntentState) === null || _state$userIntentStat === void 0 ? void 0 : _state$userIntentStat.currentUserIntent,
      editorViewMode: (_state$editorViewMode = state.editorViewModeState) === null || _state$editorViewMode === void 0 ? void 0 : _state$editorViewMode.mode
    };
  });
}, function (api) {
  var connectivityStateMode = useSharedPluginStateSelector(api, 'connectivity.mode');
  var editorToolbarDockingPreference = useSharedPluginStateSelector(api, 'userPreferences.preferences.toolbarDockingPosition');
  var currentUserIntent = useSharedPluginStateSelector(api, 'userIntent.currentUserIntent');
  var selection = useSharedPluginStateSelector(api, 'selection.selection');
  var _useSharedPluginState = useSharedPluginStateWithSelector(api, ['toolbar'], function (state) {
      var _state$toolbarState3, _state$toolbarState4;
      return {
        shouldShowToolbar: (_state$toolbarState3 = state.toolbarState) === null || _state$toolbarState3 === void 0 ? void 0 : _state$toolbarState3.shouldShowToolbar,
        selectedNode: (_state$toolbarState4 = state.toolbarState) === null || _state$toolbarState4 === void 0 ? void 0 : _state$toolbarState4.selectedNode
      };
    }),
    shouldShowToolbar = _useSharedPluginState.shouldShowToolbar,
    selectedNode = _useSharedPluginState.selectedNode;
  return {
    connectivityStateMode: connectivityStateMode,
    editorToolbarDockingPreference: editorToolbarDockingPreference,
    currentUserIntent: currentUserIntent,
    shouldShowToolbar: shouldShowToolbar,
    selection: selection,
    editorViewMode: undefined,
    selectedNode: selectedNode
  };
});
var useOnPositionCalculated = conditionalHooksFactory(function () {
  return fg('platform_editor_toolbar_aifc_patch_7');
}, function (editorView) {
  var onPositionCalculated = useCallback(function (position) {
    try {
      var toolbarTitle = SELECTION_TOOLBAR_LABEL;

      // Show special position on cell selection only when editor controls experiment is enabled
      var isEditorControlsEnabled = expValEquals('platform_editor_controls', 'cohort', 'variant1');
      var isCellSelection = ('$anchorCell' in editorView.state.selection);
      if (isCellSelection && isEditorControlsEnabled) {
        return calculateToolbarPositionOnCellSelection(toolbarTitle)(editorView, position);
      }
      return calculateToolbarPositionTrackHead(toolbarTitle)(editorView, position);
    } catch (error) {
      logException(error, {
        location: 'editor-plugin-toolbar/selectionToolbar'
      });
      return position;
    }
  }, [editorView]);
  return onPositionCalculated;
}, function (editorView) {
  var onPositionCalculated = useCallback(function (position) {
    var toolbarTitle = SELECTION_TOOLBAR_LABEL;

    // Show special position on cell selection only when editor controls experiment is enabled
    var isEditorControlsEnabled = expValEquals('platform_editor_controls', 'cohort', 'variant1');
    var isCellSelection = ('$anchorCell' in editorView.state.selection);
    if (isCellSelection && isEditorControlsEnabled) {
      return calculateToolbarPositionOnCellSelection(toolbarTitle)(editorView, position);
    }
    return calculateToolbarPositionTrackHead(toolbarTitle)(editorView, position);
  }, [editorView]);
  return onPositionCalculated;
});
export var SelectionToolbar = function SelectionToolbar(_ref) {
  var _api$toolbar;
  var api = _ref.api,
    editorView = _ref.editorView,
    mountPoint = _ref.mountPoint,
    disableSelectionToolbarWhenPinned = _ref.disableSelectionToolbarWhenPinned;
  var _usePluginState = usePluginState(api),
    connectivityStateMode = _usePluginState.connectivityStateMode,
    editorToolbarDockingPreference = _usePluginState.editorToolbarDockingPreference,
    currentUserIntent = _usePluginState.currentUserIntent,
    shouldShowToolbar = _usePluginState.shouldShowToolbar,
    editorViewMode = _usePluginState.editorViewMode,
    selection = _usePluginState.selection;
  var intl = useIntl();
  var components = api === null || api === void 0 || (_api$toolbar = api.toolbar) === null || _api$toolbar === void 0 ? void 0 : _api$toolbar.actions.getComponents();
  var toolbar = components === null || components === void 0 ? void 0 : components.find(function (component) {
    return isToolbarComponent(component);
  });
  var keyboardNavigation = useMemo(function () {
    return getKeyboardNavigationConfig(editorView, intl, api);
  }, [editorView, intl, api]);
  var _useToolbarUI = useToolbarUI(),
    isDisabled = _useToolbarUI.isDisabled;
  var patch6Enabled = expValEquals('platform_editor_toolbar_aifc_patch_6', 'isEnabled', true);
  var isOffline = connectivityStateMode === 'offline';
  var isTextSelection = !editorView.state.selection.empty && editorView.state.selection instanceof TextSelection;
  var isAllSelection = !editorView.state.selection.empty && editorView.state.selection instanceof AllSelection;
  var isCellSelection = !editorView.state.selection.empty && '$anchorCell' in editorView.state.selection;
  var onPositionCalculated = useOnPositionCalculated(editorView);
  if (expValEquals('platform_editor_toolbar_aifc_template_editor', 'isEnabled', true) && editorToolbarDockingPreference === 'top' && disableSelectionToolbarWhenPinned || !components || !toolbar) {
    return null;
  }
  if (!(isTextSelection || isCellSelection || isAllSelection) || currentUserIntent === 'dragging' || !shouldShowToolbar || currentUserIntent === 'blockMenuOpen' && expValEqualsNoExposure('platform_editor_block_menu', 'isEnabled', true) || (fg('platform_editor_toolbar_aifc_user_intent_fix') ?
  // hide toolbar when user intent is not default, except when it's dragHandleSelected without cell selection
  currentUserIntent && currentUserIntent !== 'default' && !(currentUserIntent === 'dragHandleSelected' && !isCellSelection) : currentUserIntent && currentUserIntent !== 'default') || isSSR()) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Popup, {
    offset: [0, 10],
    target: getDomRefFromSelection(editorView),
    onPositionCalculated: onPositionCalculated,
    mountTo: mountPoint
  }, /*#__PURE__*/React.createElement(EditorToolbarProvider, {
    editorView: editorView,
    editorToolbarDockingPreference: editorToolbarDockingPreference,
    editorViewMode: expValEquals('platform_editor_toolbar_aifc_patch_4', 'isEnabled', true) ? editorViewMode !== null && editorViewMode !== void 0 ? editorViewMode : 'edit' : editorViewMode,
    isOffline: isOffline
  }, /*#__PURE__*/React.createElement(EditorToolbarUIProvider, {
    api: api,
    isDisabled: patch6Enabled ? isDisabled : isOffline,
    fireAnalyticsEvent: function fireAnalyticsEvent(payload) {
      var _api$analytics;
      api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 || _api$analytics.actions.fireAnalyticsEvent(payload);
    },
    keyboardNavigation: expValEquals('platform_editor_toolbar_aifc_patch_5', 'isEnabled', true) ? keyboardNavigation : undefined
  }, /*#__PURE__*/React.createElement(ToolbarModelRenderer, {
    toolbar: toolbar,
    components: components,
    fallbacks: {
      section: ToolbarSection,
      menuSection: ToolbarDropdownItemSection,
      group: ToolbarButtonGroup
    }
  }))));
};
export var SelectionToolbarWithErrorBoundary = function SelectionToolbarWithErrorBoundary(_ref2) {
  var _api$analytics2;
  var api = _ref2.api,
    editorView = _ref2.editorView,
    mountPoint = _ref2.mountPoint,
    disableSelectionToolbarWhenPinned = _ref2.disableSelectionToolbarWhenPinned;
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    component: ACTION_SUBJECT.TOOLBAR,
    componentId: ACTION_SUBJECT_ID.SELECTION_TOOLBAR,
    dispatchAnalyticsEvent: api === null || api === void 0 || (_api$analytics2 = api.analytics) === null || _api$analytics2 === void 0 ? void 0 : _api$analytics2.actions.fireAnalyticsEvent,
    fallbackComponent: null
  }, /*#__PURE__*/React.createElement(SelectionToolbar, {
    api: api,
    editorView: editorView,
    mountPoint: mountPoint,
    disableSelectionToolbarWhenPinned: disableSelectionToolbarWhenPinned
  }));
};